function deepMerge(target, source) {
    for (const key of Object.keys(source)) {
        if (source[key] instanceof Object && key in target) {
            target[key] = deepMerge(target[key], source[key]);
        } else {
            target[key] = source[key];
        }
    }
    return target;
}

const mapHeadingToFontSize = (value, headingOverrides = {}) => {
    if (!value) return value;

    const defaultHeadingMap = {
        'h1': { base: '20px', lg: '24px' },
        'h2': { base: '16px', lg: '20px' },
        'h3': { base: '14px', lg: '16px' },
        'h4': { base: '12px', lg: '12px' },
        'h5': { base: '14px', lg: '14px' },
        'h6': { base: '12px', lg: '12px' }
    };

    // Deep merge default mappings with overrides
    const headingMap = deepMerge(defaultHeadingMap, headingOverrides);

    // Check if it's a heading tag (case-insensitive)
    const lowerValue = value.toString().toLowerCase();
    if (headingMap.hasOwnProperty(lowerValue)) {
        return headingMap[lowerValue];
    }

    // If not a heading tag, return the original value (could be xs, sm, md, lg, xl, 2xl, etc.)
    return value;
};

const mapFontFamily = (value) => {
    const mapping = {
        'Helvetica Neue LT Std': 'HelveticaNeueLTStd'
    }

    return mapping[value]
}

const mapFontStyle = (value) => {
    const mapping = {
        'bold': 'bold',
        'medium': 'medium',
        'roman': 'normal',
        'normal': 'normal',
        'italic': 'italic'
    };

    return mapping[value?.toLowerCase()] || mapping[value]
}

/**
 * Converts a styling configuration object to Chakra UI style props
 * @param {Object} styleConfig - The styling configuration object
 * @param {Object} fallback - The fallback styling configuration object
 * @param {Object} headingOverrides - Custom heading size mappings (e.g., { h1: '48px', h2: '36px' })
 * @returns {Object} Chakra UI compatible style object
 */
export const generateStyles = (styleConfig, fallback = {}, headingOverrides = {}) => {
    const styles = {};

    // Helper function to get fallback value for a given property and breakpoint
    const getFallbackValue = (property, breakpoint = null) => {
        if (!fallback || typeof fallback !== 'object') return null;

        const fallbackValue = fallback[property];
        if (!fallbackValue) return null;

        if (breakpoint && typeof fallbackValue === 'object' && fallbackValue[breakpoint]) {
            return fallbackValue[breakpoint];
        } else if (!breakpoint && typeof fallbackValue === 'string') {
            return fallbackValue;
        } else if (!breakpoint && typeof fallbackValue === 'object' && fallbackValue.base) {
            return fallbackValue.base;
        }

        return null;
    };

    // Helper function to add property if value exists, with fallback support
    const addIfExists = (chakraProperty, value, breakpoint = null, configProperty = null) => {
        // Use the configProperty name for fallback lookup if provided, otherwise use chakraProperty
        const fallbackProperty = configProperty || chakraProperty;

        // Check if we have a value from styleConfig
        const hasValue = value && value.toString().trim() !== '';

        // Get fallback value if main value doesn't exist
        const fallbackValue = !hasValue ? getFallbackValue(fallbackProperty, breakpoint) : null;

        // Use main value if it exists, otherwise use fallback
        const finalValue = hasValue ? value : fallbackValue;

        if (finalValue && finalValue.toString().trim() !== '') {
            if (breakpoint) {
                if (!styles[chakraProperty]) {
                    styles[chakraProperty] = {};
                }
                // If the property was previously set as a string, convert it to an object
                if (typeof styles[chakraProperty] === 'string') {
                    const baseValue = styles[chakraProperty];
                    styles[chakraProperty] = { base: baseValue };
                }
                styles[chakraProperty][breakpoint] = finalValue;
            } else {
                // Only set as string if no breakpoint object exists yet
                if (!styles[chakraProperty]) {
                    styles[chakraProperty] = finalValue;
                } else if (typeof styles[chakraProperty] === 'object') {
                    styles[chakraProperty].base = finalValue;
                }
            }
        }
    };

    // Base (mobile) styles
    addIfExists('fontFamily', mapFontFamily(styleConfig?.FontFamily));
    addIfExists('fontWeight', mapFontStyle(styleConfig?.FontStyle));
    addIfExists('color', styleConfig?.TextColorHex || styleConfig?.TextColor);
    addIfExists('fontSize', styleConfig?.TextSizePt ? `${styleConfig?.TextSizePt}px` : mapHeadingToFontSize(styleConfig?.TextSize, headingOverrides));
    addIfExists('textAlign', styleConfig?.TextAlignment);

    // Tablet styles
    addIfExists('fontFamily', mapFontFamily(styleConfig?.FontFamilyTablet), 'md');
    addIfExists('fontWeight', mapFontStyle(styleConfig?.FontStyleTablet), 'md');
    addIfExists('color', styleConfig?.TextColorHexTablet || styleConfig?.TextColorTablet, 'md');
    addIfExists('fontSize', styleConfig?.TextSizePtTablet ? `${styleConfig?.TextSizePtTablet}px` : mapHeadingToFontSize(styleConfig?.TextSizeTablet, headingOverrides), 'md');
    addIfExists('textAlign', styleConfig?.TextAlignmentTablet, 'md');

    // Desktop styles
    addIfExists('fontFamily', mapFontFamily(styleConfig?.FontFamilyDesktop), 'lg');
    addIfExists('fontWeight', mapFontStyle(styleConfig?.FontStyleDesktop), 'lg');
    addIfExists('color', styleConfig?.TextColorHexDesktop || styleConfig?.TextColorDesktop, 'lg');
    addIfExists('fontSize', styleConfig?.TextSizePtDesktop ? `${styleConfig?.TextSizePtDesktop}px` : mapHeadingToFontSize(styleConfig?.TextSizeDesktop, headingOverrides), 'lg');
    addIfExists('textAlign', styleConfig?.TextAlignmentDesktop, 'lg');

    // Check for any fallback properties that weren't handled above
    if (fallback && typeof fallback === 'object') {
        Object.keys(fallback).forEach(property => {
            const fallbackValue = fallback[property];

            // Skip if this property was already processed
            if (styles[property]) return;

            // Add the fallback property
            if (typeof fallbackValue === 'object') {
                styles[property] = { ...fallbackValue };
            } else {
                styles[property] = fallbackValue;
            }
        });
    }

    return styles;
};

// Alternative version that uses arrays for responsive values
export const generateStylesArray = (styleConfig, fallback = {}, headingOverrides = {}) => {
    const getValue = (base, tablet, desktop, transform = (v) => v, property = null) => {
        const values = [base, tablet, desktop].map(v =>
            v && v.toString().trim() !== '' ? transform(v) : null
        );

        // Check fallback for missing values
        if (fallback && property && fallback[property]) {
            const fallbackValue = fallback[property];
            if (Array.isArray(fallbackValue)) {
                for (let i = 0; i < Math.min(values.length, fallbackValue.length); i++) {
                    if (values[i] === null && fallbackValue[i] !== null && fallbackValue[i] !== undefined) {
                        values[i] = fallbackValue[i];
                    }
                }
            } else if (typeof fallbackValue === 'object') {
                if (values[0] === null && fallbackValue.base) values[0] = fallbackValue.base;
                if (values[1] === null && fallbackValue.md) values[1] = fallbackValue.md;
                if (values[2] === null && fallbackValue.lg) values[2] = fallbackValue.lg;
            } else if (typeof fallbackValue === 'string' && values[0] === null) {
                values[0] = fallbackValue;
            }
        }

        // Remove trailing nulls
        while (values.length > 1 && values[values.length - 1] === null) {
            values.pop();
        }

        // Return single value if only base exists, array if multiple values
        return values.length === 1 ? values[0] : values.some(v => v !== null) ? values : null;
    };

    const styles = {};

    // Font Family
    const fontFamily = getValue(styleConfig.FontFamily, styleConfig.FontFamilyTablet, styleConfig.FontFamilyDesktop, v => v, 'fontFamily');
    if (fontFamily) styles.fontFamily = fontFamily;

    // Font Style (using fontWeight for bold, italic, etc.)
    const fontStyle = getValue(styleConfig.FontStyle, styleConfig.FontStyleTablet, styleConfig.FontStyleDesktop, v => v, 'fontWeight');
    if (fontStyle) {
        // Map common font styles to fontWeight
        const styleMap = {
            'bold': 'bold',
            'medium': 'medium',
            'normal': 'normal',
            'italic': 'italic'
        };

        const mappedStyle = Array.isArray(fontStyle) ?
            fontStyle.map(s => styleMap[s] || s) :
            (styleMap[fontStyle] || fontStyle);

        // If it's italic, use fontStyle, otherwise use fontWeight
        if (mappedStyle === 'italic' || (Array.isArray(mappedStyle) && mappedStyle.includes('italic'))) {
            styles.fontStyle = fontStyle;
        } else {
            styles.fontWeight = fontStyle;
        }
    }

    // Color (prioritize hex over regular color)
    const color = getValue(
        styleConfig?.TextColorHex || styleConfig?.TextColor,
        styleConfig?.TextColorHexTablet || styleConfig?.TextColorTablet,
        styleConfig?.TextColorHexDesktop || styleConfig?.TextColorDesktop,
        v => v,
        'color'
    );
    if (color) styles.color = color;

    // Font Size (prioritize pt values, map heading tags, preserve direct values)
    const fontSize = getValue(
        styleConfig.TextSizePt ? `${styleConfig.TextSizePt}px` : mapHeadingToFontSize(styleConfig.TextSize, headingOverrides),
        styleConfig.TextSizePtTablet ? `${styleConfig.TextSizePtTablet}px` : mapHeadingToFontSize(styleConfig.TextSizeTablet, headingOverrides),
        styleConfig.TextSizePtDesktop ? `${styleConfig.TextSizePtDesktop}px` : mapHeadingToFontSize(styleConfig.TextSizeDesktop, headingOverrides),
        v => v,
        'fontSize'
    );
    if (fontSize) styles.fontSize = fontSize;

    // Text Alignment
    const textAlign = getValue(styleConfig.TextAlignment, styleConfig.TextAlignmentTablet, styleConfig.TextAlignmentDesktop, v => v, 'textAlign');
    if (textAlign) styles.textAlign = textAlign;

    // Add any remaining fallback properties
    if (fallback && typeof fallback === 'object') {
        Object.keys(fallback).forEach(property => {
            if (!styles[property]) {
                styles[property] = fallback[property];
            }
        });
    }

    return styles;
};


/**
* Merges CMS-driven category replacement and shop menu content data into a main category object.
*
* For the given main category and menu data, this function searches for matching category replacements
* and shop menu content (by CategoryId) and attaches them to the corresponding categories and subcategories
* within the main category structure. The function returns a new merged category object with the additional
* CMS data applied where matches are found.
*
* Supports multiple block types in CategoryReplacement:
* - CustomCategoryBlock: Simple category overrides (name, color, badge)
* - MegaMenuItemLevelOneBlock: Complete mega menu structure replacement
*
* @param {Object} mainCategory - The main category object to be enriched with CMS data.
* @param {Object} menuData - The CMS menu data containing CategoryReplacement and ShopMenuContent arrays.
* @returns {Object} The merged category object with CMS replacements and shop menu content applied.
*/
export const mergeCMSCategoryReplacements = (mainCategory, menuData) => {
    if (!mainCategory || !menuData) return mainCategory;

    const merged = { ...mainCategory };

    const categoryReplacements = menuData.CategoryReplacement || [];
    const shopMenuContent = menuData.ShopMenuContent || [];

    const mergeToCategory = (category) => {
        const categoryId = category.id;

        // Find matching replacement - can be CustomCategoryBlock or MegaMenuItemLevelOneBlock
        const matchingReplacement = categoryReplacements.find(item =>
            item.ContentLink?.Expanded?.CategoryId === categoryId
        );

        if (matchingReplacement) {
            const expanded = matchingReplacement.ContentLink?.Expanded;
            const blockType = expanded?.__typename;

            category.CategoryReplacement = matchingReplacement;

            // If it's a MegaMenuItemLevelOneBlock, mark category as having CMS mega menu structure
            if (blockType === 'MegaMenuItemLevelOneBlock') {
                category.hasCMSMegaMenu = true;
                category.CMSMegaMenuData = expanded;
            }
        }

        const matchingShopContent = shopMenuContent.find(item =>
            item.ContentLink?.Expanded?.CategoryId === categoryId
        );

        if (matchingShopContent) {
            category.ShopMenuContent = matchingShopContent;

            // Extract MegaMenuIlustrationBlockList from ShopMenuContent for non-CMS mega menus
            const shopContentExpanded = matchingShopContent.ContentLink?.Expanded;
            if (shopContentExpanded?.MegaMenuIlustrationBlockList?.length > 0) {
                category.MegaMenuIlustrationBlockList = shopContentExpanded.MegaMenuIlustrationBlockList;
            }
        }
    };

    if (merged.id) {
        mergeToCategory(merged);
    }

    if (Array.isArray(merged.categories)) {
        merged.categories.forEach(categoryGroup => {
            if (categoryGroup.id) {
                mergeToCategory(categoryGroup);
            }

            if (Array.isArray(categoryGroup.categories)) {
                categoryGroup.categories.forEach(subCategory => {
                    if (subCategory.id) {
                        mergeToCategory(subCategory);
                    }
                });
            }
        });
    }

    return merged;
}

/**
 * Transforms a MegaMenuItemLevelOneBlock CMS structure into a category-like structure
 * that can be rendered by the mega menu component.
 *
 * @param {Object} cmsMegaMenuData - The MegaMenuItemLevelOneBlock data from CMS
 * @param {Object} apiCategory - The original API category (for fallback data like c_relativeUrl)
 * @returns {Array} Array of category-like objects representing the menu columns
 */
export const transformMegaMenuBlockToCategories = (cmsMegaMenuData) => {
    if (!cmsMegaMenuData?.MegaMenuItemsLevelOne) {
        return [];
    }

    const columns = [];
    let illustrationBlocks = [];

    // Check for MegaMenuIlustrationBlockList directly on the MegaMenuItemLevelOneBlock
    // This is where illustration blocks are configured at the parent level
    if (cmsMegaMenuData.MegaMenuIlustrationBlockList?.length > 0) {
        illustrationBlocks = cmsMegaMenuData.MegaMenuIlustrationBlockList;
    }

    // MegaMenuItemsLevelOne contains MegaMenuItemLevelTwoBlock entries (columns)
    // It may also contain MegaMenuIlustrationBlock as siblings (check both locations)
    cmsMegaMenuData.MegaMenuItemsLevelOne.forEach((levelOneItem) => {
        const expandedBlock = levelOneItem.ContentLink?.Expanded;
        if (!expandedBlock) return;

        // Check if this is an illustration block (sibling to columns in the items array)
        if (expandedBlock.__typename === 'MegaMenuIlustrationBlock') {
            // Only add if not already found in MegaMenuIlustrationBlockList
            if (illustrationBlocks.length === 0) {
                illustrationBlocks.push(levelOneItem);
            }
            return;
        }

        // Otherwise, it should be a MegaMenuItemLevelTwoBlock (a column)
        if (expandedBlock.__typename !== 'MegaMenuItemLevelTwoBlock') {
            return;
        }

        const levelTwoBlock = expandedBlock;
        const columnCategoryId = levelTwoBlock.CategoryId?.trim();
        const columnItems = [];

        // Each item in MegaMenuItemsLevelTwo is a CustomCategoryBlock (menu link)
        if (levelTwoBlock.MegaMenuItemsLevelTwo) {
            levelTwoBlock.MegaMenuItemsLevelTwo.forEach((levelTwoItem) => {
                const customCategoryBlock = levelTwoItem.ContentLink?.Expanded;
                if (!customCategoryBlock) return;

                const itemCategoryId = customCategoryBlock.CategoryId?.trim();

                columnItems.push({
                    id: itemCategoryId,
                    name: customCategoryBlock.OverrideName || null,
                    CategoryReplacement: levelTwoItem, // Keep reference for styling
                    cmsCategoryData: customCategoryBlock, // Direct access to CMS data
                    c_relativeUrl: null, // Will be filled from API if needed
                    parentCategoryId: columnCategoryId
                });
            });
        }

        columns.push({
            id: columnCategoryId,
            name: levelTwoBlock.ColumnTitle || null, // Column header from CMS, fallback to API name
            c_showOnSeparateColumn: true,
            categories: columnItems,
            parentCategoryId: cmsMegaMenuData.CategoryId,
            CategoryReplacement: levelOneItem
        });
    });

    // Attach collected illustration blocks to the columns array for access in the component
    columns.illustrationBlocksFromParent = illustrationBlocks;

    return columns;
};

/**
* Retrieves the value of a specified attribute from a category within a category tree by category ID.
*
* Traverses the category tree recursively to find the category with the given ID and returns the value
* of the requested attribute if it exists. Returns null if the category or attribute is not found.
*
* @param {Object} categoryTree - The root of the category tree, with possible nested categories.
* @param {string|number} categoryId - The unique identifier of the category to search for.
* @param {string} attributeName - The name of the attribute to retrieve from the found category.
* @returns {*} The value of the specified attribute if found, otherwise null.
*/
const getCategoryAttribute = (categoryTree, categoryId, attributeName) => {
    if (!categoryTree || !categoryId || !attributeName) return null;

    if (categoryTree.id === categoryId) {
        return categoryTree[attributeName] || null;
    }

    const searchCategories = (categories) => {
        if (!Array.isArray(categories)) return null;

        for (const category of categories) {
            if (category.id === categoryId) {
                return category[attributeName] || null;
            }

            if (Array.isArray(category.categories)) {
                const result = searchCategories(category.categories);
                if (result !== null) return result;
            }
        }

        return null;
    };

    if (Array.isArray(categoryTree.categories)) {
        return searchCategories(categoryTree.categories);
    }

    return null;
}

/**
 * Merges category names from source data into target data based on matching IDs
 * @param {Object} sourceData - The first object containing complete category information
 * @param {Array} targetData - The second object array to be updated
 * @returns {Array} Updated target data with names filled in from source
 */
export const mergeCategoryNames = (sourceData, targetData) => {
    // Create a map of all categories from source data for quick lookup
    const categoryMap = new Map();

    const buildCategoryMap = (category) => {
        categoryMap.set(category.id, category.name);
        if (category.categories && Array.isArray(category.categories)) {
            category.categories.forEach(buildCategoryMap);
        }
    };

    // Build the map from source data
    if (sourceData.categories && Array.isArray(sourceData.categories)) {
        sourceData.categories.forEach(buildCategoryMap);
    }
    buildCategoryMap(sourceData);

    // Update target data with names from the map
    const updateTargetData = (targetCategory) => {
        // If name is null or missing, try to get it from the map
        if (!targetCategory.name && categoryMap.has(targetCategory.id)) {
            targetCategory.name = categoryMap.get(targetCategory.id);
        }

        // Process subcategories recursively
        if (targetCategory.categories && Array.isArray(targetCategory.categories)) {
            targetCategory.categories.forEach(updateTargetData);
        }

        // Process parentCategoryTree
        if (targetCategory.parentCategoryTree && Array.isArray(targetCategory.parentCategoryTree)) {
            targetCategory.parentCategoryTree.forEach((tree) => {
                if (!tree.name && categoryMap.has(tree.id)) {
                    tree.name = categoryMap.get(tree.id);
                }
            });
        }
    };

    // Update each top-level category in target
    targetData.forEach((targetCategory) => {
        updateTargetData(targetCategory);
    });

    return targetData;
}

/**
* Transforms CMS menu data into a structured category object suitable for mega menu rendering.
*
* @param {Array<Object>} cmsData - The CMS menu data array, where each item contains nested ContentLink and Expanded properties.
* @param {Object} category - The root category object, used to retrieve category attributes such as name.
* @returns {Object} A structured object representing the root category, including its id, name, MegaMenuIlustrationBlockList, and a nested categories array with level one and level two categories.
*/
export const transformCMSMenuToCategories = (cmsData, apiData) => {
    if (!Array.isArray(cmsData) || cmsData.length === 0) {
        return []
    }
    const category = apiData?.category
    const cmsDataRoot = cmsData[0]?.ContentLink?.Expanded

    const result = {
        id: cmsDataRoot?.CategoryId,
        name: getCategoryAttribute(category, category.id, 'name'),
        MegaMenuIlustrationBlockList: cmsDataRoot?.MegaMenuIlustrationBlockList,
        parentCategoryId: 'root_gar_ca',
        onlineSubCategoriesCount: cmsDataRoot?.MegaMenuItemsLevelOne?.length,
        parentCategoryTree: [{
            id: 'root_gar_ca',
            name: 'g'
        }, {
            id: cmsDataRoot?.CategoryId,
            name: getCategoryAttribute(category, cmsDataRoot?.CategoryId, 'name')
        }],
        categories: []
    }

    cmsData.forEach((menuItem) => {
        const levelOne = menuItem?.ContentLink?.Expanded?.MegaMenuItemsLevelOne

        if (Array.isArray(levelOne)) {
            levelOne.forEach((l1Item) => {
                const l1Expanded = l1Item?.ContentLink?.Expanded
                const l1CategoryId = l1Expanded?.CategoryId?.trim()

                if (l1CategoryId) {
                    const levelTwo = l1Expanded?.MegaMenuItemsLevelTwo

                    const categoryObj = {
                        CategoryReplacement: l1Item,
                        id: l1CategoryId,
                        name: l1Expanded?.OverrideName || getCategoryAttribute(category, l1CategoryId, 'name'),
                        c_showOnSeparateColumn: true,
                        parentCategoryId: cmsDataRoot?.CategoryId,
                        parentCategoryTree: [{
                            id: 'root_gar_ca',
                            name: 'g'
                        }, {
                            id: cmsDataRoot?.CategoryId,
                            name: l1Expanded?.OverrideName || getCategoryAttribute(category, cmsDataRoot?.CategoryId, 'name')
                        }],
                        onlineSubCategoriesCount: levelTwo?.length,
                        c_relativeUrl: getCategoryAttribute(category, l1CategoryId, 'c_relativeUrl'),
                        categories: []
                    }

                    if (Array.isArray(levelTwo)) {
                        levelTwo.forEach((l2Item) => {
                            const l2Expanded = l2Item?.ContentLink?.Expanded
                            const l2CategoryId = l2Expanded?.CategoryId?.trim()

                            if (l2CategoryId) {
                                categoryObj.categories.push({
                                    CategoryReplacement: l2Item,
                                    id: l2CategoryId,
                                    name: l2Expanded?.OverrideName || getCategoryAttribute(category, l2CategoryId, 'name'),
                                    c_relativeUrl: getCategoryAttribute(category, l2CategoryId, 'c_relativeUrl'),
                                    parentCategoryId: l1CategoryId,
                                    parentCategoryTree: [{
                                        id: 'root_gar_ca',
                                        name: 'g'
                                    }, {
                                        id: cmsDataRoot?.CategoryId,
                                        name: getCategoryAttribute(category, cmsDataRoot?.CategoryId, 'name')
                                    },
                                    {
                                        id: l1CategoryId,
                                        name: l1Expanded?.OverrideName || getCategoryAttribute(category, l1CategoryId, 'name')
                                    }]
                                })
                            }
                        })
                    }

                    result.categories.push(categoryObj)
                }
            })
        }
    })

    return result
}

/**
* Generates style and loading properties to conditionally hide a component on specified device types.
*
* @param {string} hideOnDevices - A comma-separated string of device names ('Mobile', 'Tablet', 'Desktop') on which the component should be hidden.
* @returns {Object} An object containing:
*   - display: An object with responsive display properties (base, md, lg) set to 'none' or 'block' based on the devices to hide.
*   - doNotLoadComponent: {boolean} Indicates whether the component should be completely omitted from rendering (true if hidden on all devices).
*/
export const getHideStyles = (hideOnDevices) => {
    if (!hideOnDevices) return {}

    const devices = hideOnDevices.split(',').map(d => d.trim())
    const display = {}
    let doNotLoadComponent = false

    if (devices.includes('Mobile')) {
        display.base = 'none'
        display.md = 'block'
        display.lg = 'block'
    }

    if (devices.includes('Tablet')) {
        display.md = 'none'
        display.lg = 'block'
    }

    if (devices.includes('Desktop')) {
        display.lg = 'none'
    }

    if (devices.includes('Mobile') && devices.includes('Tablet') && devices.includes('Desktop')) {
        doNotLoadComponent = true
    }

    // Only return display property if it has values (prevents SSR issues with empty objects)
    const result = {}
    if (Object.keys(display).length > 0) {
        result.display = display
    }
    if (doNotLoadComponent) {
        result.doNotLoadComponent = doNotLoadComponent
    }

    return result
}

/**
 * Builds a full canonical URL by concatenating the base URL with a relative path.
 *
 * @param {string} relativePath - The relative path from CMS (e.g., "/faq", "/privacy-policy")
 * @param {string} baseUrl - The base URL/origin (e.g., "https://www.garageclothing.co.uk")
 * @returns {string|null} The full canonical URL or null if no path provided
 */
const buildCanonicalUrl = (relativePath, baseUrl) => {
    if (!relativePath) return null
    if (!baseUrl) return relativePath

    let cleanBase = baseUrl.replace(/\/$/, '')
    if (!cleanBase.startsWith('http')) {
        cleanBase = `https://${cleanBase}`
    }
    const cleanPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`

    return `${cleanBase}${cleanPath}`
}

/**
 * Extracts SEO fields from Optimizely CMS page data.
 *
 * @param {Object} pageData - The CMS page data object (e.g., StandardPage, LandingPage, WebContentPage)
 * @param {string} baseUrl - The base URL/origin to prepend to canonical URL
 * @returns {Object} SEO data object with the following properties:
 *   - metaTitle: {string|null} - Page meta title
 *   - metaDescription: {string|null} - Page meta description
 *   - canonicalUrl: {string|null} - Full canonical URL for the page (baseUrl + CMS path)
 *   - excludeFromSitemap: {boolean} - Whether to exclude from sitemap
 *   - openGraphTags: {Array} - Array of OpenGraph tag objects with Property and Content fields
 *   - noIndex: {boolean} - Whether to add noindex robots tag (derived from excludeFromSitemap)
 */
export const extractSeoFromCmsPage = (pageData, baseUrl = '') => {
    if (!pageData) {
        return {
            metaTitle: null,
            metaDescription: null,
            canonicalUrl: null,
            excludeFromSitemap: false,
            openGraphTags: [],
            noIndex: false
        }
    }

    // SEO fields from Optimizely CMS SEO Property Group
    const metaTitle = pageData.MetaTitle || null
    const metaDescription = pageData.MetaDescription || null
    const excludeFromSitemap = pageData.ExcludeFromSitemap === true

    // Build full canonical URL by concatenating baseUrl with CMS path
    const canonicalUrl = buildCanonicalUrl(pageData.CanonicalUrl, baseUrl)

    // OpenGraphTags is an OpenGraphListProperty - array of objects with Property and Content
    const openGraphTags = Array.isArray(pageData.OpenGraphTags)
        ? pageData.OpenGraphTags
        : []

    return {
        metaTitle,
        metaDescription,
        canonicalUrl,
        excludeFromSitemap,
        openGraphTags,
        // If excluded from sitemap, typically should also be noindex
        noIndex: excludeFromSitemap
    }
}

/**
 * Transforms OpenGraph tags array from CMS into React Helmet compatible meta tags.
 *
 * @param {Array} openGraphTags - Array of OpenGraph tag objects with Property and Content fields
 * @returns {Array} Array of meta tag objects for React Helmet { property, content }
 */
export const transformOpenGraphTags = (openGraphTags) => {
    if (!Array.isArray(openGraphTags) || openGraphTags.length === 0) {
        return []
    }

    return openGraphTags
        .filter(tag => tag?.Property && tag?.Content)
        .map(tag => ({
            property: tag.Property,
            content: tag.Content
        }))
}
